import { GoalGroupBy, GroupByMode, PageEvents } from '@griply/types';
import { getDeadlineQuarterEndDate } from '@griply/utils';
import {
  useGoalOverviewReorder,
  useSortedGoals,
  useTimelineDropReorder,
  useUpdateGoal,
} from 'features/goals';
import {
  useGoalsOverviewFilterDocument,
  useGoalsTimelineFilterDocument,
  useUpdateUserSettings,
} from 'features/user';
import React, { RefObject, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddGoalMenuItems } from 'shared/components/connected/add-goal-menu-items';
import { PopupMenu, PopupMenuList } from 'shared/components/ui/popup-menu';
import { GoalTimelineContainer } from 'shared/components/ui/timeline';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useUser } from 'shared/hooks/use-user';
import { Goal as AppGoal, UpdateGoal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { lifeAreaOtherID } from 'shared/types/orderable-section';
import {
  TimelineScrollRequest,
  TimelineTimeframes,
} from 'shared/types/timeline';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';

import { useGoalsOverviewOrder } from '../../hooks/use-goals-overview-order';

type CategoryAddMenu = {
  categoryId: ID;
  anchorRef: RefObject<HTMLElement>;
};

export type TimelineProps = {
  timeframe: TimelineTimeframes;
  referenceDate: Timestamp;
  scrollRequest: TimelineScrollRequest;
  goals: AppGoal[];
  lifeAreas: LifeArea[];
  weekStartsOn: WeekDays;
};

export const Timeline: React.FC<TimelineProps> = ({
  timeframe,
  referenceDate,
  scrollRequest,
  weekStartsOn,
  lifeAreas,
  goals,
}) => {
  const links = useEntityLinks();
  useTrackPageVisitEvent(PageEvents.GoalsTimeline, undefined);
  const { t } = useTranslation();
  const user = useUser();
  const allGoals = useSortedGoals();
  const filterDocument = useGoalsTimelineFilterDocument();
  const overviewFilterDocument = useGoalsOverviewFilterDocument();
  // Grouping stays the Timeline's own; sortingMode is shared with the Goals list so drag order matches.
  const orderDocument = useMemo(
    () => ({
      ...filterDocument,
      sortingMode: overviewFilterDocument.sortingMode,
    }),
    [filterDocument, overviewFilterDocument.sortingMode],
  );
  const { ids: customOrder, goalChildrenByParentId } =
    useGoalsOverviewOrder(orderDocument);
  const groupBy = filterDocument.groupByMode ?? GroupByMode.None;

  const [categoryAddMenu, setCategoryAddMenu] =
    useState<CategoryAddMenu | null>(null);
  const updateUserSettings = useUpdateUserSettings();
  const { submit: updateGoal } = useUpdateGoal();
  const onReorder = useGoalOverviewReorder(
    (filterDocument.groupByMode as GoalGroupBy) ?? GroupByMode.None,
  );
  const onReorderDrop = useTimelineDropReorder(onReorder);

  const openCategoryAddMenu = (categoryId: ID, anchor: HTMLElement) =>
    setCategoryAddMenu({ categoryId, anchorRef: { current: anchor } });
  const closeCategoryAddMenu = () => setCategoryAddMenu(null);

  // Category ids are life-area ids under LifeArea grouping and quarter keys under Deadline grouping.
  const categoryLifeAreaId =
    groupBy === GroupByMode.LifeArea && categoryAddMenu
      ? categoryAddMenu.categoryId === lifeAreaOtherID
        ? null
        : categoryAddMenu.categoryId
      : undefined;
  const categoryDeadline =
    groupBy === GroupByMode.Deadline && categoryAddMenu
      ? getDeadlineQuarterEndDate(categoryAddMenu.categoryId) ?? undefined
      : undefined;

  const _updateGoal = (
    id: ID,
    { startDate, deadline }: { startDate?: Timestamp; deadline?: Timestamp },
  ) => {
    const goalToUpdate: UpdateGoal = { id };
    if (startDate) {
      goalToUpdate.startDate = startDate;
    }
    if (deadline) {
      goalToUpdate.deadline = deadline;
    }

    updateGoal(goalToUpdate);
  };

  const _updateCollapsedLifeAreas = (ids: ID[]) =>
    updateUserSettings({
      goalsOverview: { roadmap: { collapsedItems: ids } },
    });

  return (
    <>
      <GoalTimelineContainer
        links={links}
        timeframe={timeframe}
        referenceDate={referenceDate}
        scrollRequest={scrollRequest}
        goals={goals}
        allGoals={allGoals}
        lifeAreas={lifeAreas}
        weekStartsOn={weekStartsOn}
        groupBy={groupBy}
        customOrder={customOrder}
        goalChildrenByParentId={goalChildrenByParentId}
        onChangeGoal={_updateGoal}
        onReorderDrop={onReorderDrop}
        onChangeCollapsedItems={_updateCollapsedLifeAreas}
        collapsedItems={user?.settings?.goalsOverview?.roadmap?.collapsedItems}
        onCategoryAdd={openCategoryAddMenu}
        title={t('pages.goals.roadmap.timeline.title')}
      />
      {!!categoryAddMenu && (
        <PopupMenu
          referenceElement={categoryAddMenu.anchorRef}
          onOutside={closeCategoryAddMenu}
          position="bottom-start"
        >
          <PopupMenuList>
            <AddGoalMenuItems
              parentIds={[]}
              lifeAreaId={categoryLifeAreaId}
              deadline={categoryDeadline}
              onClose={closeCategoryAddMenu}
            />
          </PopupMenuList>
        </PopupMenu>
      )}
    </>
  );
};
